import styled from 'styled-components'
import { get } from 'lodash'
import React, {
  useState, useContext, useMemo, createContext,
} from 'react'

import { theme } from '../constants/theme'
import { UserContext, useUser } from '../data/UserContext'
import { disconnect } from '../utils/disconnect'
import { facmList } from '../requests/facm'
import { useListRequest, useRequest } from '../hooks/request'
import { verifyRoleFacm, verifyRoleEval, verifyRoleAdmin } from '../requests/verifyRole'
import { FACM_STATUS } from '../constants/facmStatus'
import { useInterval } from '../utils/useInterval'
import { routesDescriptions } from '../constants/routesDescriptions'
import { roleToStatusMap } from '../constants/roleToStatusMap'
import Arrow from '../img/Arrow.png'
import Disconnect from '../img/Disconnect.png'
import { grantAccess } from '../utils/grantAccess'
import { ROLES } from '../constants/roles'

import { LogoAlpps } from './icons/LogoAlpps'
import { Trad } from './common/Trad'
import { Link } from './common/Link'
import { Authorize } from './common/Authorize'
import { Margin } from './common/Margin'
import { RawButton } from './common/Button'
import { Burger } from './common/Burger'

const ItemWrapper = styled.div`
  position: relative;
  background-color: ${({ color }) => color};
`
const roleToStatusFilter = ({ user }) => [roleToStatusMap[user.role]]

const createCustomWrapper = ({ getStatusFilter }) => ({ children }) => {
  const { user } = useContext(UserContext)
  let startFilter = null
  if (user.role === ROLES.UNIT_COMMANDER) {
    startFilter = useMemo(() => ({
      facmStatus: getStatusFilter({ user }),
      cduId: user.id,
    }), [user])
  } else {
    startFilter = useMemo(() => ({
      facmStatus: getStatusFilter({ user }),
    }), [user])
  }
  const facmListRequest = useListRequest({
    startFilter,
    start: true,
    request: facmList,
  })
  useInterval({ action: facmListRequest.fetch, delay: 60 })

  const now = Date.now()
  const color = get(facmListRequest.state.data, 'docs', []).reduce((col, facm) => {
    if (col === theme.colors.red) {
      return col
    }
    if (facm.endDate < now && facm.status === FACM_STATUS.ONGOING) {
      if (facm.gdh < now) {
        return theme.colors.red
      }

      return theme.colors.warningOrange
    }

    return col
  }, '')

  const number = get(facmListRequest.state.data, 'totalDocs')

  return (
    <ItemWrapper color={color}>
      {children({ number })}
    </ItemWrapper>
  )
}

const facmInfo = {
  label: 'FACM',
  backgroundColor: theme.colors.blueFacm,
  links: [
    {
      CustomWrapper: createCustomWrapper({ getStatusFilter: () => [FACM_STATUS.ONGOING] }),
      ...routesDescriptions.facmOngoing,
    },
    {
      CustomWrapper: createCustomWrapper({ getStatusFilter: roleToStatusFilter }),
      ...routesDescriptions.facmPending,
    },
    routesDescriptions.facmAll,
  ],
}

const adminInfo = {
  links: [
    routesDescriptions.userList,
    routesDescriptions.notificationSettings,
  ],
}

const traineeInfo = {
  label: 'traineeInfo',
  backgroundColor: theme.colors.blueEval,
  links: [
    routesDescriptions.internshipList,
    routesDescriptions.gridList,
    routesDescriptions.evaluationList,
  ],
}
const disconectUser = () => {
  createContext(null)
  disconnect()
}

const openPanelOptions = {
  facm: 'facm',
  eval: 'eval',
}

export const Sidebar = () => {
  const { user } = useContext(UserContext)
  const [openPanel, setOpenPanel] = useState(openPanelOptions.facm)
  const openFacm = () => setOpenPanel(openPanelOptions.facm)
  const openEval = () => setOpenPanel(openPanelOptions.eval)
  const [open, setOpen] = useState(false)

  return (
    <>
      <Burger open={open} setOpen={setOpen} />
      <Wrapper open={open}>
        <Top>
          <CustomLogo />
        </Top>
        <MenuWrapper>
          <SidebarSection
            sectionInfo={traineeInfo}
            open={openEval}
            isOpen={openPanel === openPanelOptions.eval}
            setOpen={setOpen}
          />
          <SidebarSection
            sectionInfo={facmInfo}
            open={openFacm}
            isOpen={openPanel === openPanelOptions.facm}
            setOpen={setOpen}
          />
        </MenuWrapper>
        {openPanel === openPanelOptions.facm && <Split />}
        <SidebarSection sectionInfo={adminInfo} setOpen={setOpen} />
        <Split />
        <ProfileWrapper>
          <Link to={routesDescriptions.profile.link} onClick={() => setOpen(!open)}>
            <div>
              {user.firstName}
              {' '}
              {user.lastName}
            </div>
          </Link>

          <RawButton onClick={disconectUser}>
            <img src={Disconnect} alt="disconnect" />
          </RawButton>
        </ProfileWrapper>
        <Split />
      </Wrapper>
    </>
  )
}

const Split = styled.div`
  height: 1px;
  background-color: ${theme.colors.textGray};
`

const SidebarSection = ({
  sectionInfo, isOpen = true, open, setOpen,
}) => {
  const { user } = useUser()
  const finalLinks = sectionInfo.links && sectionInfo.links.filter(
    (link) => grantAccess({
      user,
      roles: link.roles,
      canUseInternshipPermission: link.canUseInternshipPermission,
      isInternshipDirector: link.isInternshipDirector,
    }),
  )
  const globalRoles = sectionInfo.links
    ? sectionInfo.links.reduce((roles, link) => [...roles, ...link.roles], [])
    : sectionInfo.roles

  const globalCanUseInternshipPermission = sectionInfo.links
    ? sectionInfo.links.some((link) => link.canUseInternshipPermission)
    : sectionInfo.roles

  const globalisInternshipDirector = sectionInfo.links
    ? sectionInfo.links.some((link) => link.isInternshipDirector)
    : sectionInfo.roles

  // user role verification request before displaying menu content
  if (sectionInfo === adminInfo && finalLinks.length > 0) { // display the facm part of the menu, need to check access rights to the facm BO part
    const getRequest = useRequest({
      request: () => verifyRoleAdmin(),
      start: true,
    })
    if (getRequest.state.loading) {
      return (
        <div />
      )
    }else if (!getRequest.state.data) {
      return "access denied"
    }
  }
  if (sectionInfo === traineeInfo && finalLinks.length > 0) { // display the trainee part of the menu, need to check access rights to the trainee BO part
    const getRequest = useRequest({
      request: () => verifyRoleEval(),
      start: true,
    })
    if (getRequest.state.loading) {
      return (
        <div />
      )
    }else if (!getRequest.state.data) {
      return "access denied"
    }
  }
  if (sectionInfo === facmInfo && finalLinks.length > 0) { // display the admin part of the menu, need to check access rights to the admin BO part
    const getRequest = useRequest({
      request: () => verifyRoleFacm(),
      start: true,
    })
    if (getRequest.state.loading) {
      return (
        <div />
      )
    } else if(!getRequest.state.data) {
      return "Access Denied"
    }
  }
  let display
  if (!finalLinks) {
    display = (
      <CustomLink to={sectionInfo.link} onClick={() => setOpen(!open)}>
        <SidebarSectionTitle backgroundColor={sectionInfo.backgroundColor} hoverColor={theme.colors.blueCommon}>
          <Trad id={sectionInfo.label} />
        </SidebarSectionTitle>
      </CustomLink>
    )
  } else {
    display = (
      <SidebarSectionWrapper isOpen={isOpen} linkNumber={finalLinks.length} label={sectionInfo.label}>
        {sectionInfo.label && (
          <SidebarSectionTitle onClick={open} backgroundColor={sectionInfo.backgroundColor}>
            <Trad id={sectionInfo.label} />
            <ArrowWrapper isOpen={isOpen}>
              <img src={Arrow} alt="openIndicator" />
            </ArrowWrapper>
          </SidebarSectionTitle>
        )}
        {sectionInfo.links.map((link) => {
          const linkElem = ({ number }) => (
            <CustomLink to={link.link} key={link.link} onClick={() => setOpen(false)}>
              <SidebarSectionItem hoverColor={theme.colors.blueCommon}>
                <Trad id={link.label} />
                {number > 0 && (
                  <span>
                    <Margin h={5} />
                    (
                    {number}
                    )
                  </span>
                )}
              </SidebarSectionItem>
            </CustomLink>
          )

          const finalLinkElem = link.CustomWrapper ? (
            <link.CustomWrapper>
              {linkElem}
            </link.CustomWrapper>
          ) : linkElem({})

          return link.roles ? (
            <Authorize
              roles={link.roles || []}
              canUseInternshipPermission={link.canUseInternshipPermission}
              isInternshipDirector={link.isInternshipDirector}
              key={link.link}
            >
              {finalLinkElem}
            </Authorize>
          ) : finalLinkElem
        })}
      </SidebarSectionWrapper>
    )
  }

  return (
    <Authorize
      canUseInternshipPermission={globalCanUseInternshipPermission}
      isInternshipDirector={globalisInternshipDirector}
      roles={globalRoles}
    >
      {display}
    </Authorize>
  )
}

const titleHeight = 65
const itemHeight = 60

const CustomLink = styled(Link)`
  display: block;
  &.active {
    background: ${theme.colors.grayLight};
  }
`

const ArrowWrapper = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  height: ${titleHeight}px;
  width: ${titleHeight}px;
  transform: rotate(${({ isOpen }) => (isOpen ? '180deg' : '0')});
  ${theme.transition()};
  right: 0px;
  top: 0px;
`

const SidebarSectionWrapper = styled.div`
  color: white;
  overflow: hidden;
  ${theme.transition()};
  height: ${({ isOpen, linkNumber, label }) => (isOpen
    ? itemHeight * (linkNumber + 1 - (label ? 0 : 1))
    : itemHeight)}px;
`

const SidebarSectionItem = styled.div`
  padding: 0 29px;
  height: ${itemHeight}px;
  line-height: ${itemHeight}px;
  font-size: 20px;

  color: ${theme.colors.textGray};
  ${theme.transition()};
  &:hover{
    color: ${({ hoverColor }) => hoverColor};
  }
`

const SidebarSectionTitle = styled(SidebarSectionItem)`
  background: ${({ backgroundColor }) => backgroundColor};
  font-weight: 500;
  font-size: 24px;
  display: flex;
  color: ${theme.colors.white};
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  position: relative;
`

const Wrapper = styled.div`
  background: ${theme.colors.white};
  top: 0;
  left: 0;
  bottom: 0;
  position: fixed;
  flex-direction: column;
  display: flex;
  ${theme.transition()};
  @media (min-width: 1000px) {
    width: ${theme.spacing.sidebarWidth}px;
  }
  @media (max-width: 1000px) {
    transform: ${({ open }) => (open ? 'translateX(0)' : 'translateX(-100%)')};
    transition: transform 0.3s ease-in-out;
    width: 300px;
  }
  z-index: 19;
`

const Top = styled.div`
  height: 126px;
  background: ${theme.colors.white};
  color: ${theme.colors.blueFacm}
  text-transform: uppercase;
  font-size: 26px;
  display: flex;
  padding: 36px 29px;
  align-items: center;
`

const CustomLogo = styled(LogoAlpps)`
  margin-right: 5px;
`

const MenuWrapper = styled.div`
  color: white;
  font-size: 15px;
`

const ProfileWrapper = styled.div`
  padding: 0 29px;
  height: 95px;
  color: ${theme.colors.blueCommon};
  justify-content: space-between;
  display: flex;
  font-size: 20px;
  align-items: center;
`
